import React, { useMemo } from "react";
import { Alert, Box, FormControl, FormLabel, MenuItem, Select } from "@mui/material";
import HPInput from "@components/common/Input";
import HPDropdown from "@components/common/Dropdown";
import HiddenInDatasetBadge from "@components/common/HiddenInDatasetBadge";
import { buildHiddenReasonMap } from "@utils/chart-visibility-utils";

const ALLOWED_CHARS = /^[a-zA-Z0-9 _\-]*$/;
const MAX_NAME_LENGTH = 100;

// Callers pass either Filter.ts's QuestionVariable/QuestionValue (bar chart
// edit drawer) or Dataset.ts's Variable/QuestionValue (chart creation flow) —
// structurally different id fields, so this only requires what's actually read.
interface RollupSourceVariable {
  label: string;
  title: string;
  rowtitle?: string;
  hidden?: boolean;
}

interface RollupSourceValue {
  value: string | number;
  title: string;
  label: string;
  hidden?: boolean;
}

interface RollupBuilderProps {
  focusMode: "variable" | "value";
  allVariables: RollupSourceVariable[];
  allValues: RollupSourceValue[];
  rollupName: string;
  rollupSelectedLabels: string[];
  onRollupNameChange: (name: string) => void;
  onSelectedLabelsChange: (labels: string[]) => void;
}

const RollupBuilder: React.FC<RollupBuilderProps> = ({
  focusMode,
  allVariables,
  allValues,
  rollupName,
  rollupSelectedLabels,
  onRollupNameChange,
  onSelectedLabelsChange,
}) => {
  const isVariableMode = focusMode === "variable";

  // Members already saved in an existing rollup stay in the list (so their
  // chip keeps rendering) and are tagged — but a hidden member that isn't
  // already selected is omitted entirely, not shown-and-disabled. New picks
  // only ever offer visible sources.
  const hiddenReasons = isVariableMode
    ? buildHiddenReasonMap(allVariables, (v) => v.label)
    : buildHiddenReasonMap(allValues, (v) => String(v.value));

  const isSelectedHiddenMember = (value: string) =>
    hiddenReasons.has(value) && rollupSelectedLabels.includes(value);

  const categoryOptions = isVariableMode
    ? allVariables
        .filter((v) => !v.hidden || rollupSelectedLabels.includes(v.label))
        .map((v) => ({
          value: v.label,
          label: v.rowtitle || v.title,
          subtitle: hiddenReasons.get(v.label),
        }))
    : allValues
        .filter(
          (v) =>
            !v.hidden || rollupSelectedLabels.includes(String(v.value)),
        )
        .map((v) => ({
          value: String(v.value),
          label: v.title || v.label,
          subtitle: hiddenReasons.get(String(v.value)),
        }));

  const hasHiddenSelectedMember = rollupSelectedLabels.some(
    isSelectedHiddenMember,
  );

  // A rollup needs at least 2 selectable sources (visible, or already saved in
  // this rollup). When too few remain — e.g. the rest are hidden in the
  // dataset — a new/valid rollup can't be formed; explain rather than show an
  // unusable picker.
  const hasTooFewSources = categoryOptions.length < 2;
  const sourceNoun = isVariableMode ? "variables" : "values";

  const existingNames = useMemo(() => {
    const names = new Set<string>();
    const add = (s: string | undefined) => {
      if (s?.trim()) names.add(s.trim().toLowerCase());
    };
    allVariables.forEach((v) => { add(v.title); add(v.rowtitle); add(v.label); });
    allValues.forEach((v) => { add(v.title); add(v.label); });
    return names;
  }, [allVariables, allValues]);

  const getNameError = (): string | null => {
    if (!rollupName) return null;
    const trimmed = rollupName.trim();
    if (!trimmed) return "Name is required";
    if (trimmed.length > MAX_NAME_LENGTH) return `Name must be ${MAX_NAME_LENGTH} characters or fewer`;
    if (!ALLOWED_CHARS.test(trimmed)) return "Use letters, numbers, spaces, hyphens, or underscores only";
    if (existingNames.has(trimmed.toLowerCase())) return "A category with this name already exists. Use a unique rollup name.";
    return null;
  };

  const nameError = getNameError();
  const selectionError =
    rollupSelectedLabels.length > 0 && rollupSelectedLabels.length < 2
      ? "At least 2 categories required"
      : null;

  if (hasTooFewSources) {
    return (
      <Alert severity="warning" sx={{ fontSize: "0.8125rem" }}>
        At least two visible {sourceNoun} are required for a rollup. Restore
        content in Questions to build one.
      </Alert>
    );
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <FormControl fullWidth size="small">
        <FormLabel sx={{ fontSize: 12, color: "#262626", mb: 0.5 }}>
          Rollup Type
        </FormLabel>
        <Select value="SUM_SELECTED" size="small">
          <MenuItem value="SUM_SELECTED">Combine selected categories</MenuItem>
        </Select>
      </FormControl>

      <HPInput
        label="Rollup Name*"
        value={rollupName}
        onChange={(e) => onRollupNameChange(e.target.value)}
        placeholder="Enter a name for this rollup"
        error={!!nameError}
        helperText={nameError ?? ""}
      />

      <HPDropdown
        label={`Source ${isVariableMode ? "Variables" : "Values"}* (select at least 2)`}
        value={rollupSelectedLabels}
        onChange={onSelectedLabelsChange}
        options={categoryOptions}
        multiSelect
        countLabel={isVariableMode ? "variables" : "values"}
        placeholder={`Choose ${isVariableMode ? "variables" : "values"}`}
        error={!!selectionError}
        helperText={selectionError ?? ""}
        noOptionsText={`No visible ${sourceNoun} — restore content in Questions.`}
      />
      {hasHiddenSelectedMember && <HiddenInDatasetBadge />}
    </Box>
  );
};

export default RollupBuilder;
